import React, { useState, useEffect } from "react";
import Checkmark from "@spectrum-icons/workflow/Checkmark";
import AlertCircle from "@spectrum-icons/workflow/AlertCircle";
import Pending from "@spectrum-icons/workflow/Pending";
import {
  Heading,
  Item,
  ListView,
  ActionMenu,
  Text,
} from "@adobe/react-spectrum";
import jobManager from "../jobManager";
import { useJobContext } from "../context/JobContext";
import { useNavigate } from "react-router-dom";
import allActions from "../config.json";
import actionWebInvoke, { getHeaders } from "../utils";
import { View, Grid, Audio } from "@adobe/react-spectrum";
import {
  Accordion,
  Disclosure,
  DisclosureTitle,
  DisclosurePanel,
} from "@adobe/react-spectrum";
import PaddingTop from "@spectrum-icons/workflow/PaddingTop";

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [failedJobs, setFailedJobs] = useState([]);
  const [expiredJobs, setExpiredJobs] = useState([]);

  const { selectedJob, setSelectedJob } = useJobContext();
  const navigate = useNavigate();

  const oktaAccessToken = "fake-token";

  useEffect(() => {
    const updateJobs = () => {
      setJobs(jobManager.getAllJobs());
      setCompletedJobs(jobManager.getCompletedJobs());
      setPendingJobs(jobManager.getPendingJobs());
      setFailedJobs(jobManager.getFailedJobs());
      setExpiredJobs(jobManager.getExpiredJobs());
    };

    updateJobs();

    const interval = setInterval(() => {
      updateJobs();
    }, 1000); // Check every second (adjust as needed)

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  return (
    <>
      <Heading level={2}>Voxify jobs</Heading>

      {jobs.length === 0 && <Text>No Voxify jobs found</Text>}
      {jobs.length > 0 && (
        <Accordion defaultExpandedKeys={["completed"]}>
            {pendingJobs.length > 0 && (
            <Disclosure id="pending">
              <DisclosureTitle>
                Processing ({pendingJobs.length})
              </DisclosureTitle>
              <DisclosurePanel>
                <ListView aria-label="Processing jobs">
                  {pendingJobs.map((j) => renderJob(j))}
                </ListView>
              </DisclosurePanel>
            </Disclosure>
          )}
          {completedJobs.length > 0 && (
            <Disclosure id="completed">
              <DisclosureTitle>
                Completed ({completedJobs.length})
              </DisclosureTitle>
              <DisclosurePanel>
                <ListView aria-label="Completed jobs">
                  {completedJobs.map((j) => renderJob(j))}
                </ListView>
              </DisclosurePanel>
            </Disclosure>
          )}

          {expiredJobs.length > 0 && (
            <Disclosure id="expired">
              <DisclosureTitle>Expired ({expiredJobs.length})</DisclosureTitle>
              <DisclosurePanel>
                <ListView aria-label="Expired jobs">
                  {expiredJobs.map((j) => renderJob(j))}
                </ListView>
              </DisclosurePanel>
            </Disclosure>
          )}
          {failedJobs.length > 0 && (
            <Disclosure id="failed">
              <DisclosureTitle>Failed ({failedJobs.length})</DisclosureTitle>
              <DisclosurePanel>
                <ListView aria-label="Failed jobs">
                  {failedJobs.map((j) => renderJob(j))}
                </ListView>
              </DisclosurePanel>
            </Disclosure>
          )}
        </Accordion>
      )}
    </>
  );

  function renderJob(job) {
    return (
      <Item key={job.id} textValue={job.nickname || job.text} width="100%" backgroundColor="red">
          <Grid 
          backgroundColor="blue"
            areas={[
              'icon name menu',
              'empty description description',
              'empty player player'
            ]}
            columns={['1fr', '100fr', '1fr']}
            rows={['auto', 'auto', 'auto']}
            padding="size-50"
            marginStart="size-0"
            gap="size-50">

          <View gridArea="icon">{getIcon(job)}</View>
          <View gridArea="name"><Text>{job.nickname || job.text.substring(0, 60) + (job.text.length > 60 ? "..." : "")}</Text></View>  
          <View gridArea="description">
            <Text slot="description" width="100%">
            <span className="job-status">{getStatus(job)}</span>
            <span className="job-chunks">
                {" "}
                ({job.subJobs.length} chunk{job.subJobs.length > 1 ? "s" : ""})
              </span>
            </Text>
          </View>

          <ActionMenu gridArea="menu">
            <Item
              key="load"
              textValue="Load"
              onAction={() => {
                setSelectedJob(job.id);
              }}
            >
              <Text>Load</Text>
            </Item>

            {job.status === "completed" && (
              <Item
                key="download"
                textValue="Download"
                onAction={() => {
                  job.subJobs.forEach((subJob, index) => {
                    if (subJob.url) {
                      let count = "";
                      if (job.subJobs.length > 1) {
                        count = ` (${index + 1} of ${job.subJobs.length})`;
                      }

                      downloadFile(subJob.url, `${job.nickname}${count}.wav`);
                    }
                  });
                }}
              >
                <Text>Download</Text>
              </Item>
            )}

            <Item
              key="delete"
              textValue="Delete"
              onAction={() => {
                jobManager.removeJob(job.id);
                setJobs(jobManager.getAllJobs());
              }}
            >
              <Text>Delete</Text>
            </Item>
          </ActionMenu>

          <View gridArea="player" style={{ width: '100%' }}>
          {job.status === 'completed' && (
              <audio controls style={{ display: 'block', width: '100%', height: '30px', margin: '.5rem 0' }}>
                <source src={job.subJobs[0]?.url} type="audio/wav" />
              </audio>
          )}
          </View>

        </Grid>
      </Item>
    );
  }


}

// Note: Due to S3's CORS limitations, you cannot reliably fetch the file and trigger a download with a custom name using JavaScript.
// The only thing you can do from the browser is use an <a> tag with `download` attribute, but most browsers will ignore the filename you specify
// if the destination is a cross-origin resource without proper CORS/Content-Disposition settings set on the server (which you can't control).
// Best you can do is attempt to set the download attribute and fall back to opening the direct link.

function downloadFile(url, fileName) {
  // Create an <a> element and try to use the download attribute.
  const link = document.createElement("a");
  link.href = url;
  // Attempt to set the filename
  link.setAttribute("download", fileName);
  link.target = "_blank"; // Open in new tab if download is ignored

  // Required for Firefox
  link.style.display = "none";
  document.body.appendChild(link);

  // Try to trigger download (may be ignored due to CORS or Content-Disposition)
  link.click();

  // Clean up
  document.body.removeChild(link);
}


function getIcon(job) {
  if (job.status === "completed") {
    return <Checkmark />;
  } else if (job.status === "pending") {
    return <Pending />;
  } else {
    return <AlertCircle />;
  }
}

function getStatus(job) {
  if (job.status === "pending") {
    return "Processing";
  } else {
    return job.status;
  }
}
